// Nine Lives '84 - the bookshelf room.
//
// A tall bookcase on the right with three ferns on the top shelf. Climb it
// shelf by shelf and walk into the ferns to knock them off. A spider on the
// ceiling shadows Tabs while he is on the shelves and drops on him,
// knocking him off; it climbs back up afterwards. The broom does its thing.

import { knockCat, type Cat } from '../cat';
import { DT, GRAVITY, SPIDER_H, SPIDER_W, WORLD_H } from '../constants';
import { overlaps } from '../physics';
import { Room, type RoomContext } from '../room';
import type { Box, GameEvent, Platform, RoomKind } from '../types';

export interface Fern extends Box {
  state: 'up' | 'falling' | 'down';
  vx: number;
  vy: number;
}

export interface Spider extends Box {
  state: 'lurk' | 'drop' | 'rise';
  /** Ticks before it will drop again. */
  rest: number;
}

const SHELF_X = 196;
const SHELF_W = 108;
export const SHELF_TOPS = [30, 60, 90, 120, 150];
const FERN_XS = [206, 243, 280];

export class BookshelfRoom extends Room {
  readonly kind: RoomKind = 'bookshelf';
  readonly taskName = 'ferns';
  readonly shelves: Platform[] = [];
  readonly table: Platform = { kind: 'furniture', x: 40, y: 19, w: 50, h: 3, oneWay: true };
  readonly ferns: Fern[] = [];
  readonly spider: Spider;
  knocked = 0;

  constructor(ctx: RoomContext) {
    super(ctx);
    for (const top of SHELF_TOPS) {
      this.shelves.push({ kind: 'shelf', x: SHELF_X, y: top - 3, w: SHELF_W, h: 3, oneWay: true });
    }
    const top = SHELF_TOPS[SHELF_TOPS.length - 1];
    for (const x of FERN_XS) {
      this.ferns.push({ x, y: top, w: 10, h: 12, state: 'up', vx: 0, vy: 0 });
    }
    this.platforms.push(this.table, ...this.shelves);
    this.spider = {
      x: SHELF_X + SHELF_W / 2 - SPIDER_W / 2,
      y: WORLD_H - SPIDER_H,
      w: SPIDER_W,
      h: SPIDER_H,
      state: 'lurk',
      rest: ctx.params.spiderRest,
    };
  }

  get have(): number {
    return this.knocked;
  }

  get need(): number {
    return this.ferns.length;
  }

  get topShelf(): Platform {
    return this.shelves[this.shelves.length - 1];
  }

  private onShelves(cat: Cat): boolean {
    return cat.ground !== null && cat.ground.kind === 'shelf';
  }

  protected update(cat: Cat, _keys: number, events: GameEvent[]): void {
    this.updateFerns(cat, events);
    this.updateSpider(cat, events);
  }

  private updateFerns(cat: Cat, events: GameEvent[]): void {
    for (const f of this.ferns) {
      if (f.state === 'up') {
        if (cat.ground === this.topShelf && overlaps(cat, f)) {
          f.state = 'falling';
          f.vx = cat.facing * 30;
          f.vy = 25;
          this.knocked++;
          events.push({ kind: 'fernDown', have: this.knocked, need: this.need });
          if (this.knocked >= this.need) this.done = true;
        }
      } else if (f.state === 'falling') {
        f.vy -= GRAVITY * DT;
        f.x += f.vx * DT;
        f.y += f.vy * DT;
        if (f.y <= 0) {
          f.y = 0;
          f.state = 'down';
        }
      }
    }
  }

  private updateSpider(cat: Cat, events: GameEvent[]): void {
    const s = this.spider;
    const speed = this.ctx.params.spiderSpeed;
    const ceiling = WORLD_H - s.h;
    const catCx = cat.x + cat.w / 2;
    switch (s.state) {
      case 'lurk': {
        if (s.rest > 0) s.rest--;
        if (this.onShelves(cat)) {
          // Shadow Tabs along the ceiling, then drop when lined up.
          const dx = catCx - (s.x + s.w / 2);
          const step = speed * 0.7 * DT;
          s.x += Math.abs(dx) <= step ? dx : Math.sign(dx) * step;
          if (s.rest === 0 && Math.abs(dx) < 6) s.state = 'drop';
        }
        break;
      }
      case 'drop': {
        s.y -= speed * DT;
        const floor = this.onShelves(cat) ? cat.y : 30;
        if (cat.knockedTicks === 0 && overlaps(cat, s)) {
          const dir = catCx < s.x + s.w / 2 ? -1 : 1;
          knockCat(cat, dir * 90, 60, 'spider', events);
          s.state = 'rise';
        } else if (s.y <= floor) {
          s.state = 'rise';
        }
        break;
      }
      case 'rise': {
        s.y += speed * 0.6 * DT;
        if (s.y >= ceiling) {
          s.y = ceiling;
          s.state = 'lurk';
          s.rest = this.ctx.params.spiderRest;
        }
        break;
      }
    }
  }
}
